/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import { FunctionComponent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { VodItemKernelType, VodItemType } from 'client-code/vod-browse';

import { PREVIEW_DESCRIPTION_MAX_LENGTH } from '../../../config';
import { TContentPreview, TUnifiedCollectionPreview } from '../../../core/types';
import { TVodOfferPreview } from '../../../core/types/TVodOfferPreview';
import { PreviewComponent } from '../../../core/usecases';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import { RentalImage } from './RentalImage';
import {
    AcceptablePreviewType,
    COMPONENT_VISIBILITY,
    ESTIMATED_LINE_HEIGHT,
    ESTIMATED_OPTIONAL_COMPONENT_LINES,
    FIXED_COMPONENT_KEYS,
    PREVIEW_GROUP_MAPPING,
} from './VodPreviewConfig';
import { VodPreviewFormatter } from './VodPreviewFormatter';
import { VodCompnentsType, VodPreviewProps } from './VodPreviewProps';

import './VodPreview.scss';

export type VodPreviewInfo =
    | TContentPreview
    | TUnifiedCollectionPreview
    | TVodOfferPreview['vodOfferPreview']['contentPreview'];

export const VodPreview: FunctionComponent<VodPreviewProps> = ({ screenType, item }) => {
    const [previewInfo, setPreviewInfo] = useState<VodPreviewInfo | null>(null);
    const [objectId, setObjectId] = useState<string | null>(null);
    const [partnerId, setPartnerId] = useState<string | null>(null);
    const [vodItemType, setVodItemType] = useState<string | null>(null);

    useEffect(() => {
        if (!item) {
            return;
        }
        setPartnerId(item?.vodItemKernel?.partnerId ?? null);
        setObjectId(item?.digObjectId ?? null);
        setVodItemType(item?.vodItemType ?? null);
        const kernelType = item?.vodItemKernel?.vodItemKernelType
            ? ((item?.vodItemKernel?.vodItemKernelType.charAt(0).toLowerCase() +
                  item?.vodItemKernel?.vodItemKernelType.slice(
                      1,
                  )) as (typeof VodItemKernelType)[keyof typeof VodItemKernelType])
            : undefined;

        if (kernelType === VodItemKernelType.FolderVodItemKernel) {
            setPreviewInfo(null);
            return;
        }

        const previewGroups = kernelType
            ? PREVIEW_GROUP_MAPPING[screenType]?.(kernelType) ?? []
            : [];
        const useCases = new PreviewComponent(item?.vodItemKernel);
        let previewPromise: Promise<any> | null = null;

        switch (kernelType) {
            case VodItemKernelType.ContentVodItemKernel:
                previewPromise = useCases.getVodContentPreviewInfo(previewGroups);
                break;
            case VodItemKernelType.UnifiedCollectionVodItemKernel:
                previewPromise = useCases.getVodUnifiedCollectionPreviewInfo(previewGroups);
                break;
            case VodItemKernelType.OfferVodItemKernel:
                previewPromise = useCases
                    .getVodOfferPreviewV2Info(previewGroups)
                    .then((preview) => {
                        const { vodOfferPreview } = preview;

                        // Flatten contentPreview into one object
                        return {
                            ...vodOfferPreview.contentPreview,
                            isThreeD: vodOfferPreview.isThreeD,
                            hasCc: vodOfferPreview.hasCc,
                            videoResolution: vodOfferPreview.videoResolution,
                            previewDuration: vodOfferPreview.previewDuration,
                            providerLabel: vodOfferPreview.providerLabel,
                        };
                    });
                break;
            default:
        }
        previewPromise?.then((contentPreview) => {
            setPreviewInfo(contentPreview);
        });
    }, [item, screenType]);

    const formatter = useMemo(() => {
        return previewInfo ? new VodPreviewFormatter(previewInfo, screenType) : null;
    }, [previewInfo, screenType]);

    const containerRef = useRef<HTMLDivElement>(null);
    const fullDescRef = useRef<HTMLDivElement>(null);
    const trimmedDescRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const rentalImageRef = useRef<HTMLDivElement>(null);
    const actorRef = useRef<HTMLDivElement>(null);

    const [availableParts, setAvailableParts] = useState({
        genres: true,
        duration: true,
        actors: true,
        trimCastOneLine: false,
        trimCastTwoLines: false,
        trimDescription: false,
    });
    useLayoutEffect(() => {
        if (!previewInfo || !formatter) return;
        if (!fullDescRef.current || !trimmedDescRef.current || !containerRef.current) return;

        const containerEl = containerRef.current;

        const lineHeightPx = ESTIMATED_LINE_HEIGHT;
        const containerHeight = containerEl.clientHeight;
        const rentalImageHeight = rentalImageRef.current?.clientHeight ?? 0;
        const maxAvailableLines =
            Math.floor((containerHeight - rentalImageHeight) / lineHeightPx) - 1;
        const actorHeight = actorRef.current?.clientHeight ?? 0;

        const OPTIONAL_PARTS = ['genres', 'duration', 'actors'] as const;

        // fixed component lines
        const estimatedLines = {
            fixed: 0,
            optional: ESTIMATED_OPTIONAL_COMPONENT_LINES,
        };

        let computedPreviewType: AcceptablePreviewType | undefined;
        if (previewInfo) {
            if ('previewType' in previewInfo) {
                computedPreviewType = previewInfo.previewType as AcceptablePreviewType;
            } else if ('unifiedCollectionType' in previewInfo) {
                computedPreviewType = 'UnifiedCollectionPreview';
            }
        }

        if (computedPreviewType) {
            const activeFixedLineKeys = FIXED_COMPONENT_KEYS.filter((k) =>
                COMPONENT_VISIBILITY[screenType]?.[computedPreviewType!]?.includes(k),
            );
            estimatedLines.fixed = activeFixedLineKeys.length;
        }

        // Count extra lines from multiline title - max 2 lines
        let titleExtraLines = 0;
        if (titleRef.current) {
            const titleLines = Math.floor(titleRef.current.clientHeight / lineHeightPx);
            titleExtraLines = Math.max(0, titleLines - 1);
        }
        estimatedLines.fixed += titleExtraLines;

        const fullDescEl = fullDescRef.current;
        const trimmedDescEl = trimmedDescRef.current;

        // Temporarily show full description to measure lines
        const prevDisplay = fullDescEl.style.display;
        fullDescEl.style.display = 'block';
        const fullDescLines = Math.ceil(fullDescEl.clientHeight / lineHeightPx);
        fullDescEl.style.display = prevDisplay;

        const trimmedDescLines = Math.ceil(trimmedDescEl.clientHeight / lineHeightPx);
        const availableLinesForDescAndOptional = maxAvailableLines - estimatedLines.fixed;

        // visibility flags based on what fits in the available space
        const newParts = {
            genres: false,
            duration: false,
            actors: true,
            trimDescription: true,
            trimCastOneLine: false,
            trimCastTwoLines: false,
        };

        let usedLines;
        if (fullDescLines <= availableLinesForDescAndOptional) {
            newParts.trimDescription = false;
            usedLines = fullDescLines;
        } else {
            newParts.trimDescription = true;
            usedLines = trimmedDescLines;
        }

        // Try to fit optional parts
        OPTIONAL_PARTS.forEach((key) => {
            const lines = estimatedLines.optional[key];

            if (usedLines + lines <= availableLinesForDescAndOptional) {
                if (key === 'actors') {
                    const castLines = Math.floor(actorHeight / lineHeightPx);
                    const availableSpaceForCast = availableLinesForDescAndOptional - usedLines;
                    const effectiveCastLines = Math.min(castLines, availableSpaceForCast);

                    switch (effectiveCastLines) {
                        case 0:
                            newParts.actors = false;
                            break;
                        case 1:
                            newParts.actors = false;
                            newParts.trimCastOneLine = true;
                            usedLines += 1;
                            break;
                        case 2:
                            newParts.actors = false;
                            newParts.trimCastOneLine = false;
                            newParts.trimCastTwoLines = true;
                            usedLines += 2;
                            break;
                        default:
                            newParts.actors = true;
                            newParts.trimCastOneLine = false;
                            newParts.trimCastTwoLines = false;
                            usedLines += 3;
                            break;
                    }
                } else {
                    newParts[key] = true;
                    usedLines += lines;
                }
            }
        });

        // Update state if changed
        const hasChanged = Object.keys(newParts).some(
            (key) =>
                availableParts[key as keyof typeof newParts] !==
                newParts[key as keyof typeof newParts],
        );

        if (hasChanged) {
            setAvailableParts(newParts);
        }
    }, [screenType, previewInfo, formatter, availableParts]);

    const StatusMessage: FunctionComponent = () => {
        const statusMessage = formatter?.getCombinedStatusMessage();
        if (!statusMessage) return null;

        return (
            <div className="entitlement-availability-status" data-fixed="true">
                {statusMessage}
            </div>
        );
    };

    const TrickplayRestriction: FunctionComponent = () => {
        const asset = formatter?.getTrickPlayRestrictions();
        if (!asset) {
            return null;
        }
        const trickplayLocale = formatter?.getVodTrickPlayRestriction();
        return (
            <div className="trickplay-restriction" data-fixed="true">
                {asset.imageAssetUrl ? <img src={asset.imageAssetUrl} alt="" /> : null}
                {`${trickplayLocale}`}
            </div>
        );
    };

    const Title: FunctionComponent = () => (
        <div ref={titleRef} className="content-preview-title" data-fixed="true">
            {formatter?.getFormattedTitle()}
        </div>
    );

    const EpisodeInfo: FunctionComponent = () => (
        <div className="sea-ep-title" data-fixed="true">
            {formatter?.getSeasonEpisodeTitle()}
        </div>
    );

    const Description: FunctionComponent = () => {
        const descriptionParts = formatter?.getTrimmedDescriptionParts();
        const attributesText = formatter?.getShowAttributes();
        const infoIconUrl = formatter?.getInfoImageAsset();
        const showInfoIcon =
            availableParts.trimDescription && (descriptionParts?.visible.length ?? 0) > 100;
        const allowedShowAttribute =
            previewType &&
            COMPONENT_VISIBILITY[screenType]?.[previewType]?.includes(
                VodCompnentsType.ShowAttributes,
            );

        const fullDescription = formatter?.getDescription() || '';
        const truncatedFullDescription =
            fullDescription.length > PREVIEW_DESCRIPTION_MAX_LENGTH
                ? fullDescription.substring(0, PREVIEW_DESCRIPTION_MAX_LENGTH)
                : fullDescription;

        return (
            <>
                <div
                    ref={fullDescRef}
                    className="full-content-description"
                    style={{ display: availableParts.trimDescription ? 'none' : 'block' }}
                >
                    {truncatedFullDescription}
                    {allowedShowAttribute && attributesText && <>{attributesText}</>}
                </div>
                <div
                    ref={trimmedDescRef}
                    className="trim-content-description"
                    style={{ display: availableParts.trimDescription ? 'block' : 'none' }}
                >
                    {descriptionParts?.visible}
                    <span className="fade-end">{descriptionParts?.fading}</span>
                    {allowedShowAttribute && attributesText && <>{attributesText}</>}
                    {showInfoIcon && (
                        <img className="preview-attributes-icon" src={infoIconUrl} alt="info" />
                    )}
                </div>
            </>
        );
    };

    const RatingGenres: FunctionComponent = () => {
        if (!availableParts.genres) return null;
        const internalRatings = formatter?.getInternalRatings() || [];

        const ratingNames = internalRatings
            ? PreviewComponent.getParentalRatingNames(internalRatings)
                  .filter((name) => name)
                  .map((rating) => {
                      return (
                          <span className="rating" key={rating}>
                              {rating}
                          </span>
                      );
                  })
            : [];
        return (
            <div className="parental-control-rating-genres">
                {ratingNames} <span className="genres">{formatter?.getGenres()}</span>
            </div>
        );
    };

    const Duration: FunctionComponent = () => {
        if (!availableParts.duration) return null;
        return (
            <div className="duration-star-air-date">{formatter?.getDurationInfo().join(' | ')}</div>
        );
    };

    const Cast: FunctionComponent = () => {
        let linesToShow: number = 3; // Default

        if (availableParts.trimCastOneLine) {
            linesToShow = 1;
        } else if (availableParts.trimCastTwoLines) {
            linesToShow = 2;
        } else if (availableParts.actors) {
            linesToShow = 3;
        } else {
            return null;
        }

        return (
            <div className="actors" ref={actorRef}>
                {formatter?.getCast(linesToShow)}
            </div>
        );
    };

    const ContentRentalImage: FunctionComponent = () => {
        return (
            <>
                <div className="vod-preview-image" ref={rentalImageRef}>
                    <RentalImage
                        objectId={objectId ?? ''}
                        title={formatter?.getTitle() ?? ''}
                        type={vodItemType ? vodItemType.toLowerCase() : ''}
                    />
                </div>
            </>
        );
    };

    const StarRating: FunctionComponent = () => {
        const rating = formatter?.getStarRating();
        if (!rating) return null;
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating > fullStars;
        const emptyStars = 4 - Math.ceil(rating);

        const renderStars = (type: 'full' | 'half' | 'empty', count: number) =>
            Array.from({ length: count }, (_, index) => (
                <span className={`star-icon ${type}`} key={`star-${type}-${index}`} />
            ));

        return (
            <span className="star-rating">
                {renderStars('full', fullStars)}
                {hasHalfStar && renderStars('half', 1)}
                {renderStars('empty', emptyStars)}
            </span>
        );
    };

    const DurationWithStarRating: FunctionComponent = () => {
        if (!availableParts.duration) return null;

        const durationInfo = formatter?.getDurationInfo().join(' | ');
        const rating = formatter?.getStarRating();

        if (!durationInfo && !rating) return null;

        const fullStars = rating ? Math.floor(rating) : 0;
        const hasHalfStar = rating ? rating > fullStars : false;
        const emptyStars = rating ? 4 - Math.ceil(rating) : 0;

        const renderStars = (type: 'full' | 'half' | 'empty', count: number) =>
            Array.from({ length: count }, (_, index) => (
                <span className={`star-icon ${type}`} key={`star-${type}-${index}`} />
            ));

        return (
            <div className="duration-star">
                {durationInfo}
                {rating ? (
                    <>
                        {' | '}
                        <span className="star-rating">
                            {renderStars('full', fullStars)}
                            {hasHalfStar && renderStars('half', 1)}
                            {renderStars('empty', emptyStars)}
                        </span>
                    </>
                ) : null}
            </div>
        );
    };

    const Source: FunctionComponent = useMemo(() => {
        const vodIconUrl = formatter?.getVodIcon(partnerId ?? '');
        return () => {
            const appendMovieClass =
                vodItemType?.toLowerCase() === VodItemType.MovieVodItem.toLowerCase()
                    ? ' movie'
                    : '';

            return (
                <div className={`vod-icon${appendMovieClass}`}>
                    <ImageLoader
                        src={vodIconUrl}
                        imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                    />
                </div>
            );
        };
    }, [formatter, partnerId, vodItemType]);

    if (!item) return null;
    const componentRegistry: Record<VodCompnentsType, FunctionComponent> = {
        ContentRentalImage,
        StatusMessage,
        Title,
        EpisodeInfo,
        Description,
        ShowAttributes: () => null,
        RatingGenres,
        Duration,
        StarRating,
        DurationWithStarRating,
        Cast,
        Source,
        TrickplayRestriction,
    };

    let previewType: AcceptablePreviewType | undefined;

    if (previewInfo) {
        if ('previewType' in previewInfo) {
            previewType = previewInfo.previewType as AcceptablePreviewType;
        } else if ('unifiedCollectionType' in previewInfo) {
            previewType = 'UnifiedCollectionPreview';
        }
    }
    const componentsToRender = previewType
        ? COMPONENT_VISIBILITY[screenType]?.[previewType] || []
        : [];

    return (
        <div className={`vod-preview ${screenType}`} ref={containerRef}>
            {componentsToRender.map((key) => {
                const Component = componentRegistry[key];
                return Component ? <Component key={key} /> : null;
            })}
        </div>
    );
};

export default VodPreview;
